"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { parseStepIndexFromHistory } from "./parse-step-from-history";

type UseMultiStepFormParams = {
  initialStepIndex: number;
  finalStepIndex: number;
  formId?: string;
  /**
   * Called when prevStep triggers
   * @note This method should be a stable reference
   */
  onBack?: (stepIndex: number) => void;
  /**
   * Called when the initial step is reached.
   * @note This is not called the first time the initial step is rendered, only when navigating back to it.
   * @note This method should be a stable reference
   */
  onFirst?: () => void;
  /**
   * Called when the final step is reached
   * @note This method should be a stable reference
   */
  onLast?: () => void;
  /**
   * Called when nextStep triggers
   * @note This method should be a stable reference
   */
  onNext?: (stepIndex: number) => void;
};

/*
 * Controls and tracks progress through multistep forms
 */
export const useMultiStepForm = ({
  finalStepIndex,
  formId = "step",
  initialStepIndex,
  onBack,
  onFirst,
  onLast,
  onNext,
}: UseMultiStepFormParams) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const previousStepIndexRef = useRef<number>(initialStepIndex);

  // Sync current step with browser history
  // Note: On mount we always display the initial step regardless of what is
  // stored in the history state. This is currently necessary because form
  // values are only stored in local component state and not persisted.
  // TODO: Update leadforms to persist selections.
  useEffect(() => {
    const syncFromHistory = () => {
      const stepIndexFromUrl = parseStepIndexFromHistory({
        finalStepIndex,
        formId,
        initialStepIndex,
      });

      const previousStepIndex = previousStepIndexRef.current;

      if (stepIndexFromUrl === previousStepIndex) {
        return;
      }

      setCurrentStepIndex(stepIndexFromUrl);
      previousStepIndexRef.current = stepIndexFromUrl;

      if (stepIndexFromUrl < previousStepIndex) {
        onBack?.(stepIndexFromUrl);

        if (stepIndexFromUrl === initialStepIndex) {
          onFirst?.();
        }
      } else {
        onNext?.(stepIndexFromUrl);

        if (stepIndexFromUrl === finalStepIndex) {
          onLast?.();
        }
      }
    };

    window.addEventListener("popstate", syncFromHistory);

    return () => {
      window.removeEventListener("popstate", syncFromHistory);
    };
  }, [
    finalStepIndex,
    formId,
    initialStepIndex,
    onBack,
    onFirst,
    onLast,
    onNext,
  ]);

  const goToNextStep = useCallback(() => {
    if (currentStepIndex >= finalStepIndex) {
      return;
    }

    const nextStepIndex = Math.min(currentStepIndex + 1, finalStepIndex);

    // Using the browser history API over Next.js router.push because we only
    // want to update the history state, which router.push does not support.
    // TODO: When we take on a larger refacter that works across refreshes and
    //       persists state, we will want to include URL changes and use the
    //       Next.js router
    history.pushState(
      { ...history.state, formId, stepIndex: nextStepIndex },
      "",
    );

    // Note: history.pushState will not trigger a re-render, so we need to update the
    // current step index separate from the effect
    setCurrentStepIndex(nextStepIndex);
    previousStepIndexRef.current = nextStepIndex;

    onNext?.(nextStepIndex);

    if (nextStepIndex === finalStepIndex) {
      onLast?.();
    }
  }, [currentStepIndex, finalStepIndex, formId, onLast, onNext]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex <= initialStepIndex) {
      // Maintain legacy behavior of calling onFirst when going "back" while on
      // the first step. The care giver leadform relies on this to close its
      // drawer.
      // TODO: Refactor consumers to not rely on this behavior.
      if (currentStepIndex === initialStepIndex) {
        onFirst?.();
      }

      return;
    }

    history.back();
  }, [currentStepIndex, initialStepIndex, onFirst]);

  return {
    currentStepIndex,
    goToNextStep,
    goToPreviousStep,
    isFirstStep: currentStepIndex === initialStepIndex,
    isLastStep: currentStepIndex === finalStepIndex,
  };
};
